// ============================================================================
// useMarketOverviewData.js
// ─────────────────────────────────────────────────────────────────────────────
// PURPOSE
//   Assembles the `data` prop that <MarketOverviewD3 data={...} /> expects.
//   Shape:
//     {
//       'Equities': { 'US equities': [...], 'EU equities': [...], ... }
//       'ETFs':     { 'Equity ETFs': [...], 'Bond ETFs': [...], ... }
//       'Bonds':    { 'US bonds': [...], ... }
//       'Gold':     { 'Spot gold': [...], 'Gold miners': [...], 'Gold ETFs': [...] }
//       'Crypto':   { 'Bitcoin': [...], 'Ethereum': [...], 'Altcoins': [...] }
//       // 'Indexes' intentionally omitted -- MarketOverviewD3 already reads
//       // state.marketSnapshot directly for that tab (top movers/gainers/
//       // losers/most-active); this hook only supplies the 5 categories that
//       // were previously mock-only.
//     }
//
// WIRING STATUS (2026-07-07 -- backend proxy confirmed live, see
// market_snapshots_endpoint.py's GET /market-overview/proxy-series and
// 08_MASTER_TODO.md #11)
//   [WIRED] Equities  -> SPY (US), VGK (EU), EEM (Emerging)
//   [WIRED] ETFs      -> VTI (Equity), BND (Bond), GLD (Commodity), AOR (Mixed)
//   [WIRED] Bonds     -> TLT (US), IBND (EU), VWOB (Emerging)
//           'Private debt' has no public real-time feed -- deliberately left
//           as mock (see backend allowlist comment); not guessed at.
//   [WIRED] Gold      -> XAUUSD spot, GDX (miners), GLD (ETFs)
//           'Gold futures' has no accessible feed on the current Polygon
//           plan tier -- deliberately left as mock, not substituted with
//           something misleading.
//   [WIRED] Crypto    -> BTCUSD, ETHUSD, SOLUSD (as an "Altcoins" proxy)
//           'DeFi' and 'Stablecoins' have no single representative ticker
//           worth faking -- left as mock.
//
// All of the above route through GET /market-overview/proxy-series, NOT the
// stock_data_nasdaq-backed /stocks_charts pipeline -- these proxy tickers
// (SPY, VGK, BTCUSD, etc.) are deliberately not seeded into that table; the
// backend calls Polygon directly for this fixed, allowlisted ticker set.
//
// HOW IT WORKS
//   All symbols needed for a given `days` window are fetched in ONE request
//   via MarketOverviewProxyAPI.fetchSeriesBatch (perf pass, 2026-07-18 --
//   previously each symbol was its own individual HTTP request, ~15-19 per
//   page load, each paying a constant ~200-250ms middleware tax; see
//   Technical_Docs/13_FIX_HISTORY.md's seventy-second pass), cached in-memory
//   for 10 minutes in the shared `symbolCache` below (mirrors the pattern
//   already used for watchlist mini-charts), then normalised to index-100 so
//   every line starts from the same baseline regardless of the underlying
//   asset's actual price scale.
//
// USAGE
//   const { data, isLoading } = useMarketOverviewData(activeRange);
//   <MarketOverviewD3 data={data} isLoading={isLoading} />
// ============================================================================

import { useEffect, useRef, useState, useMemo } from 'react';
import MarketOverviewProxyAPI from '../../../redux/stockData/marketOverviewProxy/marketOverviewProxyAPI';
import nasdaqIndexAPI from '../../../redux/stockData/nasdaqIndex/nasdaqIndexAPI';

// ─── CONFIG ──────────────────────────────────────────────────────────────────

// Maps MarketOverview RANGES index -> days to fetch
// Matches RANGES in MarketOverview.jsx: 1M, 3M, 6M, 1Y, 3Y, 5Y
const RANGE_TO_DAYS = [30, 90, 180, 365, 365 * 3, 365 * 5];

// category -> { subLabel (must match MarketOverview.jsx's DEFAULT_STRUCTURE exactly) -> proxy symbol }
const PROXY_MAP = {
  Equities: {
    'US equities': 'SPY',
    'EU equities': 'VGK',
    'Emerging equities': 'EEM',
  },
  ETFs: {
    'Equity ETFs': 'VTI',
    'Bond ETFs': 'BND',
    'Commodity ETFs': 'GLD',
    'Mixed ETFs': 'AOR',
  },
  Bonds: {
    'US bonds': 'TLT',
    'EU bonds': 'IBND',
    'Emerging bonds': 'VWOB',
    // 'Private debt' has no public feed -- omitted, stays mock.
  },
  Gold: {
    'Spot gold': 'XAUUSD',
    'Gold miners': 'GDX',
    'Gold ETFs': 'GLD',
    // 'Gold futures' not accessible on the current Polygon plan -- omitted, stays mock.
  },
  Crypto: {
    Bitcoin: 'BTCUSD',
    Ethereum: 'ETHUSD',
    Altcoins: 'SOLUSD',
    // 'DeFi' / 'Stablecoins' have no single representative ticker -- omitted, stay mock.
  },
};

// Flat { symbol: true } for the fetch loop
const ALL_SYMBOLS = [...new Set(Object.values(PROXY_MAP).flatMap((cat) => Object.values(cat)))];

// Local cache TTL in ms (10 minutes -- these are daily bars, don't change fast)
const CACHE_TTL_MS = 10 * 60 * 1000;

// ─── SHARED SYMBOL CACHE + IN-FLIGHT BATCH DE-DUP (perf pass, 2026-07-18) ────
// Module-level, not per-hook: useMarketOverviewData and useIndexesExtraData
// are mounted together by MarketOverview.jsx and both need SPY. Previously
// each hook fetched its own copy of every symbol individually (~19 separate
// HTTP requests per page load, each paying a constant ~200-250ms middleware
// tax -- the actual root cause of this widget's slowness, not missing/broken
// caching). Now both hooks share one cache and route through
// MarketOverviewProxyAPI.fetchSeriesBatch, so all of one hook's symbols go
// out in a single request, and a symbol already in-flight for the other hook
// (SPY) is awaited instead of re-fetched. See Technical_Docs/13_FIX_HISTORY.md's
// seventy-second pass.
const symbolCache = {}; // symbol -> { bars, fetchedAt, days }
const inFlightBySymbol = {}; // symbol -> Promise (resolves once symbolCache[symbol] is populated)

function isSymbolFresh(symbol, days) {
  const entry = symbolCache[symbol];
  if (!entry?.fetchedAt) return false;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) return false;
  if (entry.days !== days) return false;
  return true;
}

// Ensures every symbol in `symbols` is present (fresh) in symbolCache for
// `days`, firing at most one batch request for whatever isn't already fresh
// or already in flight (registered synchronously, before any await, so a
// sibling hook's overlapping call in the same render pass sees it).
async function ensureSymbolsFetched(symbols, days) {
  const needed = symbols.filter((sym) => !isSymbolFresh(sym, days) && !inFlightBySymbol[sym]);

  if (needed.length) {
    const promise = MarketOverviewProxyAPI.fetchSeriesBatch(needed, days)
      .then((result) => {
        for (const sym of needed) {
          symbolCache[sym] = { bars: result[sym] ?? [], fetchedAt: Date.now(), days };
        }
      })
      .catch((err) => {
        console.warn('[useMarketOverviewData] Batch fetch failed:', err.message);
        for (const sym of needed) {
          const stale = symbolCache[sym]?.bars ?? [];
          symbolCache[sym] = { bars: stale, fetchedAt: Date.now(), days };
        }
      })
      .finally(() => {
        for (const sym of needed) delete inFlightBySymbol[sym];
      });

    for (const sym of needed) inFlightBySymbol[sym] = promise;
  }

  const toAwait = [...new Set(symbols.map((sym) => inFlightBySymbol[sym]).filter(Boolean))];
  if (toAwait.length) await Promise.all(toAwait);
}

// ─── NORMALISE TO INDEX-100 ───────────────────────────────────────────────────
// [{date: 'YYYY-MM-DD', close: number}] -> [{date: Date, val: number}] indexed
// to 100 at the first available point, so every line starts level regardless
// of the underlying asset's price scale (a $60,000 BTC line and a $70 ETF
// line need to share one y-axis meaningfully).
function normaliseToIndex100(rawBars) {
  if (!rawBars?.length) return [];

  const pairs = rawBars
    .map((b) => ({ date: new Date(b.date), close: parseFloat(b.close) }))
    .filter((b) => !isNaN(b.date) && b.close > 0)
    .sort((a, b) => a.date - b.date);

  if (!pairs.length) return [];

  const base = pairs[0].close;
  return pairs.map((b) => ({
    date: b.date,
    val: parseFloat(((b.close / base) * 100).toFixed(2)),
  }));
}

// ─── HOOK ────────────────────────────────────────────────────────────────────
/**
 * @param {number} activeRange  Index into RANGES array (0-5). Pass from MarketOverview.
 * @returns {{ data: object|null, isLoading: boolean }}
 */
export function useMarketOverviewData(activeRange = 3) {
  const days = RANGE_TO_DAYS[activeRange] ?? 365;

  const [tick, setTick] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    ensureSymbolsFetched(ALL_SYMBOLS, days).then(() => {
      if (cancelled) return;
      setIsLoading(false);
      setTick((n) => n + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [days]);

  const data = useMemo(() => {
    const out = {};

    for (const [category, subMap] of Object.entries(PROXY_MAP)) {
      const catData = {};
      for (const [label, symbol] of Object.entries(subMap)) {
        const bars = symbolCache[symbol]?.bars ?? [];
        const normalised = normaliseToIndex100(bars);
        if (normalised.length) catData[label] = normalised;
      }
      if (Object.keys(catData).length) out[category] = catData;
    }

    return Object.keys(out).length ? out : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, tick]);

  return { data, isLoading };
}

// ─── INDEXES TAB EXTRA SUB-TABS (#14) ─────────────────────────────────────────
// 'Indexes' is deliberately excluded from PROXY_MAP/useMarketOverviewData above
// -- MarketOverview.jsx merges its 'Indexes' key from marketSnapshot (Top
// Movers/Gainers/Losers/Most Active) via a SEPARATE code path, and a naive
// `{...indexesPart, ...externalData}` spread would let an `externalData.Indexes`
// key silently clobber that real data instead of adding to it. So this is its
// own small hook, returned separately and merged explicitly by the caller
// alongside liveIndexesData, rather than folded into the shared PROXY_MAP.
//
// S&P 500 (SPY), Dow Jones (DIA), and Volatility/VIX (VIXY) all go through the
// same GET /market-overview/proxy-series used above -- Polygon returns 403 for
// the real I:SPX/I:DJI/I:VIX index tickers on this app's current plan tier
// (confirmed live), so these are real, liquid ETF proxies, not synthetic data.
// Nasdaq is different: I:NDX already works directly (no plan restriction), so
// it's fetched via the real index-price endpoint instead of a proxy.
const INDEXES_PROXY_SYMBOLS = { 'S&P 500': 'SPY', 'Dow Jones': 'DIA', 'Volatility (VIX)': 'VIXY' };
const NASDAQ_INDEX_TICKER = 'I:NDX';

export function useIndexesExtraData(activeRange = 3) {
  const days = RANGE_TO_DAYS[activeRange] ?? 365;
  const [tick, setTick] = useState(0);
  // Nasdaq isn't a PROXY_TICKER_ALLOWLIST symbol (it goes through the real
  // I:NDX index endpoint, not the proxy-series-batch path), so it keeps its
  // own tiny cache instead of living in the shared symbolCache.
  const nasdaqCacheRef = useRef(null); // { bars, fetchedAt, days }

  useEffect(() => {
    let cancelled = false;
    const proxySymbols = Object.values(INDEXES_PROXY_SYMBOLS); // ['SPY', 'DIA', 'VIXY']

    const now = Date.now();
    const nasdaqStale =
      !nasdaqCacheRef.current?.fetchedAt ||
      now - nasdaqCacheRef.current.fetchedAt > CACHE_TTL_MS ||
      nasdaqCacheRef.current.days !== days;

    const tasks = [ensureSymbolsFetched(proxySymbols, days)];

    if (nasdaqStale) {
      tasks.push(
        nasdaqIndexAPI
          .fetchIndexPrices(NASDAQ_INDEX_TICKER, { limit: days })
          .then((raw) => {
            nasdaqCacheRef.current = {
              bars: (raw || []).map((p) => ({ date: p.timestamp, close: p.close })),
              fetchedAt: Date.now(),
              days,
            };
          })
          .catch((err) => {
            console.warn('[useIndexesExtraData] Failed to fetch Nasdaq:', err.message);
            nasdaqCacheRef.current = {
              bars: nasdaqCacheRef.current?.bars ?? [],
              fetchedAt: Date.now(),
              days,
            };
          })
      );
    }

    Promise.all(tasks).then(() => {
      if (cancelled) return;
      setTick((n) => n + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [days]);

  return useMemo(() => {
    const out = {};
    for (const [label, symbol] of Object.entries(INDEXES_PROXY_SYMBOLS)) {
      const bars = symbolCache[symbol]?.bars ?? [];
      const normalised = normaliseToIndex100(bars);
      if (normalised.length) out[label] = normalised;
    }
    const nasdaqNormalised = normaliseToIndex100(nasdaqCacheRef.current?.bars ?? []);
    if (nasdaqNormalised.length) out['Nasdaq'] = nasdaqNormalised;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, tick]);
}
